import { ReactElement, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { InstallationInfoPatch } from "../../types/requesterTypes";
import {
  InstallationInfo,
  InstallationSamplingRates,
} from "../../types/types";
import { Requester } from "../../utils/Requester";
import PortalModalHeader from "../PortalModal/PortalModalHeader";
import PortalModalContent from "../PortalModal/PortalModalContent";
import PortalModalFooter from "../PortalModal/PortalModalFooter";
import { ModalButton } from "../PortalModal/ModalButton";
import { SimpleSelect } from "../Misc/SimpleSelect";

interface Props {
  installation: InstallationInfo;
  device_id: string;
  onUpdate: () => void;
  close: () => void;
}

type SampleRates = { value: InstallationSamplingRates; label: string };

export function InstallationConfigModal({
  installation,
  device_id,
  onUpdate,
  close,
}: Props): ReactElement {
  const { t } = useTranslation();

  const forcedSamplingRate = installation.sampling_rate.forced
  const samplingRate = installation.sampling_rate.interval
  const options: Array<SampleRates> = useMemo(() => {
    const list = [
      {
        value: InstallationSamplingRates.RATE1,
        label: `5 ${t("minutes")} (${t("additional cost applies")})`,
      },
      {
        value: InstallationSamplingRates.RATE2,
        label: `15 ${t("minutes")} (${t("additional cost applies")})`,
      },
      {
        value: InstallationSamplingRates.RATE3,
        label: `1 ${t("hour")}`,
      },
    ]
    const rate = list.find((a) => a.value === samplingRate)
    if(!rate) {
      list.push({
        value: samplingRate,
        label: `${t("Custom interval")}: ${Math.floor(samplingRate / 60)} min ${samplingRate % 60} sec`,
      })
    }
    return list
  }, [t, samplingRate]);

  const initialRate = options.find((a) => a.value === samplingRate)
  const [newRate, setRate] = useState<SampleRates | undefined>(initialRate);

  return <>
    <PortalModalHeader
      title={t("Configure {{installation_name}} ({{installation_id}})", {
        installation_name: installation.name,
        installation_id: device_id })}
      close={close}
    />
    <PortalModalContent>
      <h3 className="mt-2">{t("Sampling interval")}</h3>
      <SimpleSelect
        isDisabled={forcedSamplingRate}
        selectedOption={newRate}
        options={options}
        getOptionLabel={option => option.label}
        onChange={option => setRate(option)}
      />
    </PortalModalContent>
    <PortalModalFooter>
      <ModalButton
        onClick={() => {
          if (!newRate || forcedSamplingRate) return;
          const patchObject: InstallationInfoPatch = {
            installationID: installation.pk,
            sampling_rate: newRate.value,
          };

          void Requester.patchInstallationInfo(patchObject)
            .then(() => toast.success(
              t("Installation settings updated!").toString()
            ))
            .then(() => onUpdate())
            .catch(() => toast.error("Failed updating settings!")); // There is no backend written in the case that saving settings fails, so this part is an unreachable.

          close();
        }}
      >
        {t("Save")}
      </ModalButton>
    </PortalModalFooter>
  </>
}
